/**
 * Admin Service
 * Super admin CRUD operations for users, news, marketplace, and visibility overrides
 */

import { db, auth } from '@/lib/firebase';
import { getIdToken } from 'firebase/auth';

/** Returns the current user's Firebase ID token for admin API calls. */
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return getIdToken(user, /* forceRefresh= */ false);
}

/** Fetch with Authorization header pre-attached. */
async function adminFetch(url: string, opts: RequestInit = {}): Promise<Response> {
  const token = await getAuthToken();
  return fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers as Record<string, string>),
      Authorization: `Bearer ${token}`,
    },
  });
}
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  limit,
  where,
  writeBatch,
  documentId,
} from 'firebase/firestore';
import { propagateReleaseCoverById } from './cover-index';

/** Delete all favorites that reference a specific item */
async function cascadeDeleteFavorites(itemType: string, itemId: string): Promise<void> {
  try {
    const favSnap = await getDocs(
      query(collection(db, 'favorites'), where('itemType', '==', itemType), where('itemId', '==', itemId))
    );
    if (!favSnap.empty) {
      const batch = writeBatch(db);
      favSnap.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
    }
  } catch {
    // Non-critical — log silently
  }
}

// ============ USERS ============

/**
 * Batch-fetch current profile images for a list of user IDs.
 * Uses documentId() 'in' queries (max 30 per batch).
 * Returns a map of userId → profileImageUrl (may be '' if not set).
 */
export async function batchGetUserImages(userIds: string[]): Promise<Record<string, string>> {
  if (!userIds.length) return {};
  const result: Record<string, string> = {};
  const unique = Array.from(new Set(userIds));
  for (let i = 0; i < unique.length; i += 30) {
    const chunk = unique.slice(i, i + 30);
    try {
      const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
      snap.docs.forEach(d => {
        const data = d.data();
        result[d.id] = data.profileImageUrl || data.photoURL || data.imageUrl || '';
      });
    } catch { /* non-fatal */ }
  }
  return result;
}

/**
 * BB Crew — the inner circle. These users automatically see each other
 * and the Super Admin in their chat contacts without needing to follow.
 * Not labelled anywhere in the UI; Super Admin can also set isBBCrew on
 * any user via the admin panel.
 */
export const BB_CREW_EMAILS: string[] = [
  'breakinbreadswe@gmail.com',
  'gordoncyrus@gmail.com',
  'donskills@gmail.com',
  'desmondfoster@gmail.com',
  'sequo.cyrus@gmail.com',
];

export function isBBCrewEmail(email?: string | null): boolean {
  if (!email) return false;
  return BB_CREW_EMAILS.includes(email.toLowerCase());
}

export interface AdminUser {
  id: string;
  email?: string;
  displayName?: string;
  profileImageUrl?: string;
  photoURL?: string;
  imageUrl?: string;
  accountType?: string;
  roles?: string[];
  hidden?: boolean;
  adminHidden?: boolean;
  isBBCrew?: boolean;
  status?: string;
  createdAt?: string;
  lastLogin?: string;
  [key: string]: any;
}

/**
 * Returns all BB Crew users — matches on isBBCrew flag OR known email list.
 * Caller should filter out themselves.
 */
export async function getBBCrewUsers(): Promise<AdminUser[]> {
  try {
    const snapshot = await getDocs(collection(db, 'users'));
    const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as AdminUser));
    return all.filter(u => u.isBBCrew === true || isBBCrewEmail(u.email));
  } catch (error) {
    console.error('Error fetching BB Crew users:', error);
    return [];
  }
}

export async function getAllUsers(): Promise<AdminUser[]> {
  // Do NOT orderBy('displayName') at the query level: Firestore EXCLUDES any
  // doc that lacks the ordered field, so ghost / contributor accounts created
  // without a displayName (e.g. "Joe Watts") silently disappeared from the
  // list — which is why connected contributors weren't showing in the release
  // / artist editors. Fetch everyone, then sort client-side so nobody is lost.
  try {
    const snapshot = await getDocs(collection(db, 'users'));
    const users = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as AdminUser));
    return users.sort((a, b) =>
      ((a.displayName || a.email || '') as string).toLowerCase()
        .localeCompare(((b.displayName || b.email || '') as string).toLowerCase())
    );
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
}

export async function updateUser(id: string, data: Partial<AdminUser>): Promise<boolean> {
  try {
    // Route through Admin SDK to bypass Firestore rules — admin status may come
    // from Firestore roles rather than a custom claim on the auth token, so
    // client-side updateDoc on another user's document can be silently denied.
    const { id: _, ...updateData } = data;
    const res = await adminFetch('/api/admin/update-user', {
      method: 'POST',
      body: JSON.stringify({ userId: id, data: updateData }),
    });
    return res.ok;
  } catch (error) {
    console.error('Error updating user:', error);
    return false;
  }
}

export async function toggleUserAdminHidden(id: string, hidden: boolean): Promise<boolean> {
  return updateUser(id, { adminHidden: hidden });
}

export async function deleteUser(id: string): Promise<boolean> {
  try {
    const res = await adminFetch('/api/admin/delete-user', {
      method: 'POST',
      body: JSON.stringify({ userId: id }),
    });
    return res.ok;
  } catch (error) {
    console.error('Error deleting user:', error);
    return false;
  }
}

export interface InviteUserResult {
  success: boolean;
  userId?: string;
  inviteLink?: string | null;
  /** True when the server actually dispatched the invite email (Resend). */
  emailSent?: boolean;
  emailError?: string | null;
  error?: string;
}

/**
 * Create a Firebase Auth user with a specific email + password.
 * The user can immediately sign in with these credentials.
 */
export async function createUserWithPassword(
  email: string,
  password: string,
  displayName?: string,
  accountType?: string,
): Promise<InviteUserResult> {
  try {
    const res = await adminFetch('/api/admin/create-user', {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName, accountType }),
    });
    const data = await res.json();
    if (!res.ok) return { success: false, error: data.error };
    return { success: true, ...data };
  } catch {
    return { success: false, error: 'Network error' };
  }
}

/**
 * Create a lightweight, claimable contributor placeholder (a "ghost" user doc,
 * no auth account, hidden from listings). Used by the artist Connected Accounts
 * panel to add a credited contributor who isn't on BB yet — they can be invited
 * later (inviteUser) or claim the account themselves. Fully reversible.
 */
export async function createPlaceholderContributor(
  displayName: string,
  email?: string,
): Promise<{ id: string; displayName: string; email?: string }> {
  const ref = await addDoc(collection(db, 'users'), {
    displayName: displayName.trim(),
    ...(email?.trim() ? { email: email.trim().toLowerCase() } : {}),
    accountType: 'ghost',
    roles: ['Creator'],
    hidden: true,
    placeholder: true,
    createdViaConnect: true,
    onboardingComplete: false,
    createdAt: serverTimestamp(),
  });
  return { id: ref.id, displayName: displayName.trim(), email: email?.trim() || undefined };
}

/**
 * Resolve a typed name to a real BB account — for flows (e.g. Rights & Splits'
 * "Add Participant") that need a userId but only have a name someone typed,
 * with no fallback to create one (Gordon 2026-09-30: "add and notify. if the
 * user doesn't exist create the person").
 *
 * Order of preference:
 *  1. An existing `users` doc with this exact display name.
 *  2. An `artists` doc with this exact name — "sometimes artist and user is
 *     the same name" (Gordon). If that artist profile is claimed
 *     (`ownerUid` set), resolve to the real owner's account. If unclaimed,
 *     create a placeholder ghost seeded with the artist's photo, so the new
 *     account is recognizable rather than a bare name.
 *  3. Nothing found — create a bare placeholder ghost account.
 */
export async function resolveOrCreatePerson(
  name: string,
  email?: string,
): Promise<{ id: string; displayName: string; profileImageUrl?: string; email?: string }> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Name is required');

  // 1. Exact-match an existing registered user.
  try {
    const userSnap = await getDocs(query(collection(db, 'users'), where('displayName', '==', trimmed)));
    if (!userSnap.empty) {
      const d = userSnap.docs[0];
      const data = d.data() as Record<string, unknown>;
      return {
        id: d.id,
        displayName: (data.displayName as string) || trimmed,
        profileImageUrl: (data.profileImageUrl || data.photoURL || data.avatarUrl || '') as string,
        email: (data.email as string) || undefined,
      };
    }
  } catch { /* users read may be denied for non-admins — fall through */ }

  // 2. Exact-match an artist profile with the same name.
  try {
    const artistSnap = await getDocs(query(collection(db, 'artists'), where('name', '==', trimmed)));
    if (!artistSnap.empty) {
      const artist = artistSnap.docs[0].data() as Record<string, unknown>;
      const ownerUid = (artist.ownerUid as string) || '';
      if (ownerUid) {
        try {
          const ownerSnap = await getDoc(doc(db, 'users', ownerUid));
          if (ownerSnap.exists()) {
            const data = ownerSnap.data() as Record<string, unknown>;
            return {
              id: ownerSnap.id,
              displayName: (data.displayName as string) || trimmed,
              profileImageUrl: (data.profileImageUrl || data.photoURL || (artist.imageUrl as string) || '') as string,
              email: (data.email as string) || undefined,
            };
          }
        } catch { /* fall through to placeholder */ }
      }
      // Unclaimed artist profile — seed a placeholder with their photo.
      const ghost = await createPlaceholderContributor(trimmed, email);
      const imageUrl = (artist.imageUrl as string) || '';
      if (imageUrl) {
        try { await updateDoc(doc(db, 'users', ghost.id), { profileImageUrl: imageUrl, artistId: artistSnap.docs[0].id }); } catch { /* non-fatal */ }
      }
      return { ...ghost, profileImageUrl: imageUrl || undefined };
    }
  } catch { /* artists read failed — fall through to a bare placeholder */ }

  // 3. Nobody matched — create a bare placeholder ghost account.
  const ghost = await createPlaceholderContributor(trimmed, email);
  return ghost;
}

export async function inviteUser(
  email: string,
  displayName?: string,
  accountType?: string
): Promise<InviteUserResult> {
  try {
    const res = await adminFetch('/api/admin/invite-user', {
      method: 'POST',
      body: JSON.stringify({ email, displayName, accountType }),
    });
    const data = await res.json();
    if (!res.ok) return { success: false, error: data.error };
    // Server returns `resetLink`; normalise to `inviteLink` so UI can always display it
    return {
      success: true,
      userId: data.userId,
      inviteLink: data.inviteLink ?? data.resetLink ?? null,
      emailSent: data.emailSent ?? false,
      emailError: data.emailError ?? null,
    };
  } catch (error) {
    return { success: false, error: 'Network error' };
  }
}

/**
 * Generate (or re-generate) a magic sign-in link for an already-existing user record.
 * Does NOT create a new Firestore doc — use inviteUser() for brand-new accounts.
 */
export async function sendInviteForUser(
  email: string,
  displayName?: string,
  accountType?: string,
): Promise<InviteUserResult> {
  try {
    const res = await adminFetch('/api/admin/resend-invite', {
      method: 'POST',
      body: JSON.stringify({ email, displayName, accountType }),
    });
    const data = await res.json();
    if (!res.ok) return { success: false, error: data.error };
    return { success: true, ...data };
  } catch {
    return { success: false, error: 'Network error' };
  }
}

// ============ NEWS ============

export interface AdminNewsArticle {
  id: string;
  title?: string;
  slug?: string;
  content?: string;
  excerpt?: string;
  category?: string;
  author?: string;
  publishedAt?: any;
  featuredImageUrl?: string;
  status?: string;
  tags?: string[];
  [key: string]: any;
}

export async function getAllNews(): Promise<AdminNewsArticle[]> {
  try {
    const q = query(collection(db, 'news'), orderBy('publishedAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as AdminNewsArticle));
  } catch (error) {
    console.error('Error fetching news:', error);
    try {
      const snapshot = await getDocs(collection(db, 'news'));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as AdminNewsArticle));
    } catch { return []; }
  }
}

export async function updateNewsArticle(id: string, data: Partial<AdminNewsArticle>): Promise<boolean> {
  try {
    const { id: _, ...updateData } = data;
    await updateDoc(doc(db, 'news', id), { ...updateData, updatedAt: serverTimestamp() });
    return true;
  } catch (error) {
    console.error('Error updating news article:', error);
    return false;
  }
}

export async function deleteNewsArticle(id: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, 'news', id));
    await cascadeDeleteFavorites('news', id);
    return true;
  } catch (error) {
    console.error('Error deleting news article:', error);
    return false;
  }
}

export async function createNewsArticle(data: Partial<AdminNewsArticle>): Promise<string | null> {
  try {
    const docRef = await addDoc(collection(db, 'news'), {
      ...data,
      status: data.status || 'published',
      // publishedAt must be a Firestore Timestamp so getNewsArticles() orderBy works.
      // data.publishedAt may already be set by the editor; serverTimestamp() is the fallback.
      publishedAt: (data as any).publishedAt || serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating news article:', error);
    return null;
  }
}

// ============ MARKETPLACE ============

export interface AdminMarketplaceItem {
  id: string;
  title?: string;
  description?: string;
  price?: number;
  currency?: string;
  type?: string;
  categoryKey?: string;
  media?: string[];
  status?: string;
  hidden?: boolean;
  adminHidden?: boolean;
  [key: string]: any;
}

export async function getAllMarketplaceItems(): Promise<AdminMarketplaceItem[]> {
  try {
    const snapshot = await getDocs(collection(db, 'item'));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as AdminMarketplaceItem));
  } catch (error) {
    console.error('Error fetching marketplace items:', error);
    return [];
  }
}

export async function updateMarketplaceItem(id: string, data: Partial<AdminMarketplaceItem>): Promise<boolean> {
  try {
    const { id: _, ...updateData } = data;
    await updateDoc(doc(db, 'item', id), { ...updateData, updatedAt: new Date().toISOString() });
    return true;
  } catch (error) {
    console.error('Error updating marketplace item:', error);
    return false;
  }
}

export async function deleteMarketplaceItem(id: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, 'item', id));
    await cascadeDeleteFavorites('item', id);
    return true;
  } catch (error) {
    console.error('Error deleting marketplace item:', error);
    return false;
  }
}

// ============ MEDIA/WORKS ============

export async function getAllMedia(): Promise<any[]> {
  try {
    const snapshot = await getDocs(collection(db, 'works'));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching all media:', error);
    return [];
  }
}

export async function updateMedia(id: string, data: any): Promise<boolean> {
  try {
    const { id: _, ...updateData } = data;
    await updateDoc(doc(db, 'works', id), { ...updateData, updatedAt: new Date().toISOString() });
    return true;
  } catch (error) {
    console.error('Error updating media:', error);
    return false;
  }
}

export async function deleteMedia(id: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, 'works', id));
    await cascadeDeleteFavorites('media', id);
    return true;
  } catch (error) {
    console.error('Error deleting media:', error);
    return false;
  }
}

// ============ EVENTS ============

export async function createEvent(data: any): Promise<string | null> {
  try {
    const docRef = await addDoc(collection(db, 'events'), {
      ...data,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating event:', error);
    return null;
  }
}

export async function getAllEvents(): Promise<any[]> {
  try {
    const q = query(collection(db, 'events'), orderBy('startTime', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching events:', error);
    try {
      const snapshot = await getDocs(collection(db, 'events'));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch { return []; }
  }
}

export async function updateEvent(id: string, data: any): Promise<boolean> {
  try {
    const { id: _, ...updateData } = data;
    await updateDoc(doc(db, 'events', id), { ...updateData, updatedAt: new Date().toISOString() });
    return true;
  } catch (error) {
    console.error('Error updating event:', error);
    return false;
  }
}

export async function deleteEvent(id: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, 'events', id));
    await cascadeDeleteFavorites('event', id);
    return true;
  } catch (error) {
    console.error('Error deleting event:', error);
    return false;
  }
}

export async function toggleEventAdminHidden(id: string, hidden: boolean): Promise<boolean> {
  try {
    await updateDoc(doc(db, 'events', id), { adminHidden: hidden, updatedAt: new Date().toISOString() });
    return true;
  } catch (error) {
    console.error('Error toggling event visibility:', error);
    return false;
  }
}

// ============ SYNC TRACKS ============

export async function createSyncTrack(data: any): Promise<string | null> {
  try {
    const docRef = await addDoc(collection(db, 'syncTracks'), {
      ...data,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating sync track:', error);
    return null;
  }
}

export async function getAllSyncTracks(): Promise<any[]> {
  try {
    const snapshot = await getDocs(collection(db, 'syncTracks'));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching sync tracks:', error);
    return [];
  }
}

export async function updateSyncTrack(id: string, data: any): Promise<boolean> {
  try {
    const { id: _, ...updateData } = data;
    await updateDoc(doc(db, 'syncTracks', id), { ...updateData, updatedAt: new Date().toISOString() });
    return true;
  } catch (error) {
    console.error('Error updating sync track:', error);
    return false;
  }
}

export async function deleteSyncTrack(id: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, 'syncTracks', id));
    await cascadeDeleteFavorites('sync', id);
    return true;
  } catch (error) {
    console.error('Error deleting sync track:', error);
    return false;
  }
}

export async function toggleSyncTrackAdminHidden(id: string, hidden: boolean): Promise<boolean> {
  try {
    await updateDoc(doc(db, 'syncTracks', id), { adminHidden: hidden, updatedAt: new Date().toISOString() });
    return true;
  } catch (error) {
    console.error('Error toggling sync track visibility:', error);
    return false;
  }
}

// ============ RELEASES (Albums, Singles, EPs) ============

export async function createRelease(data: any): Promise<string | null> {
  try {
    // Strip undefined values — Firestore rejects them, including nested fields inside tracks[].
    function stripUndefined(obj: any): any {
      if (Array.isArray(obj)) return obj.map(stripUndefined);
      if (obj !== null && typeof obj === 'object') {
        const out: Record<string, any> = {};
        for (const [k, v] of Object.entries(obj)) {
          if (v !== undefined) out[k] = stripUndefined(v);
        }
        return out;
      }
      return obj;
    }
    const docRef = await addDoc(collection(db, 'releases'), {
      ...stripUndefined(data),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    // If the release's artist is attached to user(s), connect them to the
    // release + its tracks (so it lands on the right person's profile).
    try {
      const { connectArtistToReleaseAndTracks } = await import('./artist-link');
      const workIds = Array.isArray(data?.tracks)
        ? data.tracks.map((t: any) => t?.workId || t?.id).filter(Boolean)
        : [];
      await connectArtistToReleaseAndTracks({ releaseId: docRef.id, artistId: data?.artistId, workIds });
    } catch { /* non-fatal */ }
    // Tracks without artwork get the release cover (fills empty only).
    void propagateReleaseCoverById(docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error creating release:', error);
    return null;
  }
}

export async function getAllReleases(): Promise<any[]> {
  try {
    const snapshot = await getDocs(collection(db, 'releases'));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching releases:', error);
    return [];
  }
}

export async function updateRelease(id: string, data: any): Promise<boolean> {
  try {
    const { id: _, ...updateData } = data;

    // Deep-strip undefined values — Firestore rejects any field set to undefined,
    // including nested fields inside tracks[] objects.
    function stripUndefined(obj: any): any {
      if (Array.isArray(obj)) return obj.map(stripUndefined);
      if (obj !== null && typeof obj === 'object') {
        const out: Record<string, any> = {};
        for (const [k, v] of Object.entries(obj)) {
          if (v !== undefined) out[k] = stripUndefined(v);
        }
        return out;
      }
      return obj;
    }

    // Credited people stay findable from their profile (works keep the same index).
    if (Array.isArray(updateData.contributors)) {
      updateData.contributorUserIds = [...new Set(updateData.contributors.map((c: any) => c?.userId).filter(Boolean))];
    }
    await updateDoc(doc(db, 'releases', id), { ...stripUndefined(updateData), updatedAt: new Date().toISOString() });

    // See updateWork in services/media.ts for why this event exists — lets
    // FavoritesBar refresh a stale header thumbnail without re-favoriting.
    const dispatchThumbUpdate = (itemType: string, itemId: string) => {
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('bb:thumbnail-updated', { detail: { itemType, itemId } }));
        }
      } catch { /* non-fatal */ }
    };
    if ('coverArtUrl' in updateData) dispatchThumbUpdate('release', id);

    // Propagate new cover art to all linked work docs so their own coverArtUrl
    // field is updated — this ensures track thumbnails reflect the release cover
    // everywhere (even in lists that don't use the coverMap pattern).
    const newCoverArt: string = updateData.coverArtUrl || '';
    if (newCoverArt) {
      const tracks: any[] = updateData.tracks || [];
      const workIds = tracks.map((t: any) => t.workId || t.id).filter(Boolean) as string[];
      if (workIds.length > 0) {
        try {
          const batch = writeBatch(db);
          workIds.forEach((wid: string) => {
            batch.update(doc(db, 'works', wid), {
              coverArtUrl: newCoverArt,
              updatedAt: new Date().toISOString(),
            });
          });
          await batch.commit();
          workIds.forEach(wid => dispatchThumbUpdate('track', wid));
        } catch {
          // Non-critical — release doc is already saved; log and continue
          console.warn('Could not propagate coverArtUrl to linked works');
        }
      }
    }
    // A partial edit (cover only, or tracks only) used to skip the copy above;
    // fill any track that still has no artwork from the saved release.
    if ('coverArtUrl' in updateData || 'images' in updateData || 'tracks' in updateData) void propagateReleaseCoverById(id);

    return true;
  } catch (error) {
    console.error('Error updating release:', error);
    return false;
  }
}

export async function deleteRelease(id: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, 'releases', id));
    await cascadeDeleteFavorites('release', id);
    return true;
  } catch (error) {
    console.error('Error deleting release:', error);
    return false;
  }
}

export async function toggleReleaseAdminHidden(id: string, hidden: boolean): Promise<boolean> {
  try {
    await updateDoc(doc(db, 'releases', id), { adminHidden: hidden, updatedAt: new Date().toISOString() });
    return true;
  } catch (error) {
    console.error('Error toggling release visibility:', error);
    return false;
  }
}

// ============ MARKETPLACE ITEM VISIBILITY ============

export async function toggleMarketplaceAdminHidden(id: string, hidden: boolean): Promise<boolean> {
  try {
    await updateDoc(doc(db, 'item', id), { adminHidden: hidden, updatedAt: new Date().toISOString() });
    return true;
  } catch (error) {
    console.error('Error toggling marketplace visibility:', error);
    return false;
  }
}

// ============ MEDIA VAULT HELPERS ============

/**
 * Save an image URL to the media vault (works collection) so it's visible
 * in the Media Vault listing. Called automatically when admins upload images.
 */
export async function saveImageToMediaVault(imageUrl: string, title: string, uploadedByUid?: string): Promise<void> {
  try {
    await addDoc(collection(db, 'works'), {
      title: title || 'Untitled Image',
      type: 'Image',
      coverArtUrl: imageUrl,
      imageUrl,
      status: 'Published',
      privacy: 'Private',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      uploadedBy: uploadedByUid || '',
    });
  } catch (error) {
    console.error('Error saving image to media vault:', error);
  }
}

// ============ VIDEOS ============

export async function getAllVideos(): Promise<any[]> {
  try {
    const snapshot = await getDocs(collection(db, 'videos'));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching all videos:', error);
    return [];
  }
}

// ============ CREATOR EVENTS (Creator Self-Management) ============

export async function getCreatorEvents(userId: string): Promise<any[]> {
  try {
    const q = query(collection(db, 'events'), where('organizerId', '==', userId), orderBy('startTime', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    try {
      const q2 = query(collection(db, 'events'), where('organizerId', '==', userId));
      const snapshot = await getDocs(q2);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch { return []; }
  }
}

export async function createEventForCreator(data: any, userId: string): Promise<string | null> {
  try {
    const docRef = await addDoc(collection(db, 'events'), {
      ...data,
      organizerId: userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating event:', error);
    return null;
  }
}
